// /patient — landing page after a patient logs in.
// Mirrors the mockup home: greeting + hero card + today's reminders +
// quick actions + pre-visit welcome block.
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  doctorLabel,
  firstNameOf,
  formatLongDate,
  formatTime,
  usePatientData,
  useUpcomingAndPast,
  untilLabel,
} from "./PatientDataContext";
import { getPatientPageVisitPathSegment } from "../../utils/visitPathUtils";
import PatientReportUpload from "../../components/patient/PatientReportUpload";

export default function PatientHome() {
  const navigate = useNavigate();
  const { userRow, rows } = usePatientData();
  const { upcoming } = useUpcomingAndPast(rows);
  const nextVisit = upcoming[0] || null;
  const totalVisits = rows.length;
  const isVerified = userRow?.patient_match_status === "verified";

  const openVisit = (row) => {
    const seg = getPatientPageVisitPathSegment(row);
    if (!seg) return;
    navigate(`/visit/${seg}`, {
      state: {
        rowData: row,
        date_: row.date_,
        name: row.patient_name || row.name || "Unknown",
      },
    });
  };

  const greeting = firstNameOf(userRow);
  const doctor = doctorLabel(rows[0] || {});

  return (
    <>
      {/* HERO CARD — pre-visit CTA */}
      {nextVisit ? (
        <div className="pp-hero-card">
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="pp-hero-eyebrow">
              {untilLabel(nextVisit.date_)} · 5 min
            </div>
            <div className="pp-hero-title">
              Tell {doctor} what's going on
            </div>
            <div className="pp-hero-meta">
              A short conversation. Your answers go right into your chart.
            </div>
          </div>
          <button
            type="button"
            className="pp-hero-cta"
            onClick={() => openVisit(nextVisit)}
          >
            Start →
          </button>
        </div>
      ) : (
        <div className="pp-card pp-tinted-teal">
          <div className="pp-visit-eyebrow">
            {totalVisits ? "All caught up" : "Welcome"}
          </div>
          <div className="pp-visit-title">
            {totalVisits ? "No upcoming visits" : "No visits on file yet"}
          </div>
          <p className="pp-detail-p" style={{ marginTop: 8, marginBottom: 0 }}>
            {totalVisits
              ? "When your clinic schedules your next appointment, it will show up here."
              : isVerified
              ? "Add your first appointment or check back once your clinic schedules one."
              : "Your account isn't linked to a clinic record yet, so no visits can be shown. You can still add an appointment."}
          </p>
        </div>
      )}

      {/* TODAY'S REMINDERS */}
      <div className="pp-section-title">Today's reminders</div>
      <div className="pp-reminder-grid">
        <Link to="meds" className="pp-reminder-card pp-reminder-card-link">
          <div className="pp-reminder-leading">℞</div>
          <div className="pp-reminder-body">
            <div className="pp-reminder-title">Morning medicines</div>
            <div className="pp-reminder-meta">
              3 pills · take with breakfast
            </div>
          </div>
          <span className="pp-row-pill pp-coral">DUE</span>
        </Link>
        <Link to="vitals" className="pp-reminder-card pp-reminder-card-link">
          <div className="pp-reminder-leading pp-coral">♡</div>
          <div className="pp-reminder-body">
            <div className="pp-reminder-title">Log your blood pressure</div>
            <div className="pp-reminder-meta">
              {doctor} asked for daily readings
            </div>
          </div>
          <span className="pp-row-chev">›</span>
        </Link>
      </div>

      {/* UPLOAD A REPORT — goes to the next visit's folder AND the patient's
          chart folder, so the clinic sees it either way (the backend handles
          the dual filing). Requires an approved account: without a linked
          chart there is nowhere to file it, so the button is disabled rather
          than failing after the file picker. */}
      <div className="pp-section-title">Share a report</div>
      <div className="pp-card">
        <div className="pp-visit-title">Send a report to your care team</div>
        <p className="pp-detail-p" style={{ marginTop: 8 }}>
          {isVerified
            ? "Lab results, imaging, records from another clinic — upload them here and they'll be waiting on your chart" +
              (nextVisit ? " and attached to your next visit." : ".")
            : "Once the clinic links your account to your chart, you'll be able to upload reports here."}
        </p>
        <div style={{ marginTop: 14 }}>
          <PatientReportUpload
            disabled={!isVerified}
            buttonClassName="pp-btn pp-coral pp-btn-compact"
            buttonLabel="⬆ Upload a report"
          />
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <div className="pp-section-title">Quick actions</div>
      <div className="pp-tile-grid">
        <Link to="appointments" className="pp-tile pp-t-teal">
          <div className="pp-tile-icon">📅</div>
          <div>
            <div className="pp-tile-title">Appointments</div>
            <div className="pp-tile-meta">
              {upcoming.length === 1 ? "1 upcoming" : `${upcoming.length} upcoming`}
              {` · ${totalVisits} total`}
            </div>
          </div>
        </Link>
        <Link to="message" className="pp-tile pp-t-coral">
          <div className="pp-tile-icon">✉</div>
          <div>
            <div className="pp-tile-title">Message clinic</div>
            <div className="pp-tile-meta">{`Norinne & ${doctor}`}</div>
          </div>
        </Link>
        <Link to="labs" className="pp-tile pp-t-amber">
          <div className="pp-tile-icon">🧪</div>
          <div>
            <div className="pp-tile-title">Lab results</div>
            <div className="pp-tile-meta">2 new since last visit</div>
          </div>
        </Link>
        <Link to="chart" className="pp-tile pp-t-green">
          <div className="pp-tile-icon">📋</div>
          <div>
            <div className="pp-tile-title">My chart</div>
            <div className="pp-tile-meta">Summary & history</div>
          </div>
        </Link>
      </div>

      {/* PRE-VISIT WELCOME BLOCK */}
      {nextVisit && (
        <div className="pp-card pp-previsit">
          <div className="pp-previsit-head">
            <span className="pp-previsit-eyebrow">Before your visit</span>
          </div>
          <div className="pp-previsit-title">
            {doctor} · {formatLongDate(nextVisit.date_)}
          </div>

          <div className="pp-msg-bubble">
            <div className="pp-msg-meta">💬 From VitaMed · text message</div>
            <p>
              Hi {greeting}. You have an appointment with {doctor} on{" "}
              {formatLongDate(nextVisit.date_)}
              {nextVisit.time_ ? ` at ${formatTime(nextVisit.time_)}` : ""}. Can you
              tell us why you're coming in — in your own words? It takes about 5
              minutes and your doctor will read it before you walk in.
            </p>
            <div className="pp-msg-link">vitamed.app/v/{nextVisit.id || "aB7-3K"}</div>
          </div>

          <p className="pp-detail-p pp-previsit-lede">
            <strong>Five minutes that save your visit.</strong> A friendly
            conversation about what's going on with you. Your answers go
            straight into your chart — so when your doctor walks in, you don't
            have to start from the beginning.
          </p>

          <div className="pp-previsit-cols">
            <div className="pp-previsit-col">
              <div className="pp-previsit-col-h">📋 Why we ask</div>
              <p className="pp-detail-p" style={{ margin: 0 }}>
                You shouldn't have to tell the form, then the nurse, then the
                doctor — losing pieces each time. Tell us once, well. That's the
                whole idea.
              </p>
            </div>
            <div className="pp-previsit-col">
              <div className="pp-previsit-col-h">🔒 Three promises</div>
              <ul className="pp-previsit-list">
                <li>We <strong>never</strong> use your information to train AI.</li>
                <li>We <strong>never</strong> show you ads.</li>
                <li>We <strong>never</strong> sell your data.</li>
              </ul>
            </div>
          </div>

          <div className="pp-previsit-col">
            <div className="pp-previsit-col-h">What we'll cover</div>
            <ul className="pp-previsit-list pp-previsit-checks">
              <li><span>🤔</span> What's bringing you in</li>
              <li><span>🕒</span> When it started and how it feels</li>
              <li><span>💊</span> Any medication changes</li>
              <li><span>❓</span> Questions you want {doctor} to answer</li>
            </ul>
          </div>

          <label className="pp-previsit-consent">
            <input type="checkbox" defaultChecked />
            <span>I understand my answers become part of my medical chart.</span>
          </label>

          <div className="pp-previsit-actions">
            <button
              type="button"
              className="pp-btn pp-teal"
              onClick={() => openVisit(nextVisit)}
            >
              → Let's start
            </button>
            <Link to="appointments" className="pp-btn pp-ghost">
              Skip for now
            </Link>
          </div>
        </div>
      )}
    </>
  );
}